import { ACTIVE_ITEMS } from '@constants/activeItems';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel } from '@mui/material';
import { RootContext } from 'components/RootContainer';
import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 10;

export const CashRegisterField: FC<{
  value: number[];
  onChange: (value: number[]) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation('reservation');
  const [selected, setSelected] = useState<Array<number>>([]);
  const {
    HotelApi: {
      settings: {
        financial: {
          cashRegister: { getAll },
        },
      },
    },
  } = useContext(RootContext);

  const { isLoading, data } = useQuery({
    queryKey: ['get-cash-registers'],
    queryFn: () => {
      return getAll({
        search: [ACTIVE_ITEMS],
      });
    },
  });

  const handleChange = (cashierId: number | 'all') => {
    const allIds = data?.items?.map(cashier => cashier.id!) || [];
    if (cashierId === 'all') {
      setSelected(allIds.length === selected.length ? [] : allIds);
      onChange([]);
      return;
    }

    // Toggle individual cashier selection

    const newSelected = selected.includes(cashierId)
      ? selected.filter(id => id !== cashierId)
      : [...selected, cashierId];
    setSelected(newSelected);
    onChange(newSelected.length === allIds.length ? [] : newSelected);
  };

  if (isLoading) {
    return <>...</>;
  }

  const allRegisterIds = data?.items?.map(register => register.id!) || [];
  const allSelected =
    allRegisterIds.length > 0 && allRegisterIds.every(id => selected.includes(id));

  return (
    <Box
      sx={{
        boxShadow: 'inset 0 -1px 0 0 #d2d7de',
        pb: 2,
      }}
    >
      <FormControl variant="standard" fullWidth>
        <InputLabel shrink>Cash Registers</InputLabel>
        <Box
          sx={{
            mt: 3,
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            overflow: 'auto',
          }}
        >
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={() => handleChange('all')}
                  sx={{ mr: 1 }}
                />
              }
              label={t('list.filter.all')}
              sx={{ py: 0.5, pl: 1.5 }}
            />
            {(data?.items || [])?.map(register => (
              <FormControlLabel
                key={register.id}
                control={
                  <Checkbox
                    checked={selected.includes(register.id!)}
                    onChange={() => handleChange(register.id!)}
                    sx={{ mr: 1 }}
                  />
                }
                label={register.code}
                sx={{ py: 0.5, pl: 1.5 }}
              />
            ))}
          </FormGroup>
        </Box>
      </FormControl>
    </Box>
  );
};
